import fs from 'fs';
import path from 'path';

// Determine if we're running on server
const isSSR = () => typeof window === 'undefined';

// Only define fileURLToPath in server environment
let fileURLToPath;
if (isSSR()) {
  try {
    // Dynamic import to avoid client-side errors
    import('url').then(urlModule => {
      fileURLToPath = urlModule.fileURLToPath;
    });
  } catch (error) {
    console.error('Error importing url module:', error);
  }
}

// Cache TTL in milliseconds (30 minutes)
export const CACHE_TTL = 30 * 60 * 1000;

// Simple in-memory cache
const memoryCache = {
  posts: {},
  singlePosts: {}
};

// Generate a cache key from parameters
export const generateBlogCacheKey = (params) => {
  try {
    return `blog_${JSON.stringify(params)}`;
  } catch (e) {
    console.error('Error generating cache key:', e);
    return 'blog_default';
  }
};

// Get cache directory
export const getCacheDir = () => {
  if (!isSSR()) return null;
  
  try {
    // In Node.js environment, use process.cwd() instead of fileURLToPath
    return path.resolve(process.cwd(), '.cache/blog');
  } catch (error) {
    console.error('Error getting cache directory:', error);
    return null;
  }
};

// Ensure cache directory exists
export const ensureCacheDir = () => {
  if (!isSSR()) return;
  
  try {
    const cacheDir = getCacheDir();
    if (!cacheDir) return;
    
    if (!fs.existsSync(cacheDir)) {
      fs.mkdirSync(cacheDir, { recursive: true });
    }
    console.log(`[Blog Cache] Cache directory ensured at: ${cacheDir}`);
  } catch (error) {
    console.error('Error ensuring cache directory exists:', error);
  }
};

// Blog posts functions
export const getBlogPostsFromServerCache = (cacheKey) => {
  if (!isSSR()) return { data: null, isValid: false };
  
  const cachedItem = memoryCache.posts[cacheKey];
  if (cachedItem && Date.now() - cachedItem.timestamp < CACHE_TTL) {
    return { data: cachedItem.data, isValid: true };
  }
  
  // Try to load from file if running in Node
  try {
    const cacheDir = getCacheDir();
    if (!cacheDir) return { data: null, isValid: false };
    
    const cacheFile = path.join(cacheDir, `${cacheKey.replace(/[^a-z0-9]/gi, '_')}.json`);
    
    if (fs.existsSync(cacheFile)) {
      const fileData = JSON.parse(fs.readFileSync(cacheFile, 'utf8'));
      if (Date.now() - fileData.timestamp < CACHE_TTL) {
        // Store in memory for future requests
        memoryCache.posts[cacheKey] = fileData;
        return { data: fileData.data, isValid: true };
      }
    }
  } catch (error) {
    console.error('Error loading from file cache:', error);
  }
  
  return { data: null, isValid: false };
};

export const saveBlogPostsToServerCache = (cacheKey, data) => {
  if (!isSSR()) return;
  
  const cacheItem = {
    data,
    timestamp: Date.now()
  };
  
  // Save to memory
  memoryCache.posts[cacheKey] = cacheItem;
  
  // Save to file if running in Node
  try {
    const cacheDir = getCacheDir();
    if (!cacheDir) return;
    
    // Ensure cache directory exists
    if (!fs.existsSync(cacheDir)) {
      fs.mkdirSync(cacheDir, { recursive: true });
    }
    
    const cacheFile = path.join(cacheDir, `${cacheKey.replace(/[^a-z0-9]/gi, '_')}.json`);
    fs.writeFileSync(cacheFile, JSON.stringify(cacheItem), 'utf8');
  } catch (error) {
    console.error('Error saving to file cache:', error);
  }
};

// Single post functions
export const getSinglePostFromServerCache = (slug) => {
  if (!isSSR()) return { data: null, isValid: false };
  
  const cachedItem = memoryCache.singlePosts[slug];
  if (cachedItem && Date.now() - cachedItem.timestamp < CACHE_TTL) {
    return { data: cachedItem.data, isValid: true };
  }
  
  // Try to load from file if running in Node
  try {
    const cacheDir = getCacheDir();
    if (!cacheDir) return { data: null, isValid: false };
    
    const cacheFile = path.join(cacheDir, `post_${slug.replace(/[^a-z0-9]/gi, '_')}.json`);
    
    if (fs.existsSync(cacheFile)) {
      const fileData = JSON.parse(fs.readFileSync(cacheFile, 'utf8'));
      if (Date.now() - fileData.timestamp < CACHE_TTL) {
        // Store in memory for future requests
        memoryCache.singlePosts[slug] = fileData;
        return { data: fileData.data, isValid: true };
      }
    }
  } catch (error) {
    console.error('Error loading from file cache:', error);
  }
  
  return { data: null, isValid: false };
};

export const saveSinglePostToServerCache = (slug, data) => {
  if (!isSSR()) return;
  
  const cacheItem = {
    data,
    timestamp: Date.now()
  };
  
  // Save to memory
  memoryCache.singlePosts[slug] = cacheItem;
  
  // Save to file if running in Node
  try {
    const cacheDir = getCacheDir();
    if (!cacheDir) return;
    
    // Ensure cache directory exists
    if (!fs.existsSync(cacheDir)) {
      fs.mkdirSync(cacheDir, { recursive: true });
    }
    
    const cacheFile = path.join(cacheDir, `post_${slug.replace(/[^a-z0-9]/gi, '_')}.json`);
    fs.writeFileSync(cacheFile, JSON.stringify(cacheItem), 'utf8');
  } catch (error) {
    console.error('Error saving to file cache:', error);
  }
};

// Clear all blog cache
export const clearBlogCache = () => {
  // Clear memory cache
  memoryCache.posts = {};
  memoryCache.singlePosts = {};
  
  // Clear file cache if running in Node
  try {
    const cacheDir = getCacheDir();
    if (!cacheDir) return;
    
    if (fs.existsSync(cacheDir)) {
      const files = fs.readdirSync(cacheDir);
      for (const file of files) {
        fs.unlinkSync(path.join(cacheDir, file));
      }
    }
  } catch (error) {
    console.error('Error clearing file cache:', error);
  }
};

// Seed the blog cache with initial data
export const seedBlogCache = async (posts = []) => {
  try {
    if (!isSSR()) return;
    
    console.log(`[Blog Cache] Seeding cache with ${posts.length} posts`);
    
    // Save posts to cache with a default key
    saveBlogPostsToServerCache('blog_initial', posts);
    
    // Save each post individually
    for (const post of posts) {
      if (post.slug) {
        saveSinglePostToServerCache(post.slug, post);
      }
    }
    
    console.log('[Blog Cache] Cache seeded successfully');
  } catch (error) {
    console.error('[Blog Cache] Error seeding cache:', error);
  }
};

// Re-export for compatibility
export const generateCacheKey = generateBlogCacheKey;
